import {type ClassValue, clsx} from "clsx"
import {twMerge} from "tailwind-merge"

// computeFileHash.js
import SparkMD5 from 'spark-md5';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function matchYoutubeUrl(url: string) {
    if (!url) return false;
    let p = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    if(url.match(p)){
        return url.match(p)[1];
    }
    return false;
}

export const formatLocalTime = (utcTime: any) => {
    const date = new Date(utcTime);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    });
};

export const formatLocalDate = (utcTime: any) => {
    const date = new Date(utcTime);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Converts a binary string to a Base64-encoded string.
 * @param {string} binary - The binary string.
 * @returns {string} - The Base64-encoded string.
 */
function binaryStringToBase64(binary) {
    // Convert binary string to an array of bytes
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    // Convert bytes to Base64
    let binaryStr = '';
    bytes.forEach(byte => binaryStr += String.fromCharCode(byte));
    return btoa(binaryStr);
}

/**
 * Computes the MD5 hash of a file and returns it in Base64 format.
 * This hash is compatible with Active Storage's blob checksum.
 * @param {File} file - The file to hash.
 * @param {number} [chunkSize=4194304] - The size of each chunk in bytes (default: 300 MB).
 * @returns {Promise<string>} - A promise that resolves to the Base64-encoded MD5 hash.
 */
export async function computeFileHash(file, chunkSize = 300 * 1024 * 1024) { // 4MB chunks
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error('No file provided.'));
            return;
        }

        const blobSlice = file.slice || file.mozSlice || file.webkitSlice;
        const totalChunks = Math.ceil(file.size / chunkSize);
        let currentChunk = 0;
        const spark = new SparkMD5.ArrayBuffer();
        const fileReader = new FileReader();

        fileReader.onload = function (e) {
            // @ts-ignore
            spark.append(e.target.result); // Append array buffer
            currentChunk++;

            if (currentChunk < totalChunks) {
                loadNext();
            } else {
                // All chunks loaded, compute the hash
                const rawHash = spark.end(true); // Raw binary string
                const base64Hash = binaryStringToBase64(rawHash);
                resolve(base64Hash);
            }
        };

        fileReader.onerror = function () {
            reject(new Error('Error reading file.'));
        };

        function loadNext() {
            const start = currentChunk * chunkSize;
            const end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;
            const chunk = blobSlice.call(file, start, end);
            fileReader.readAsArrayBuffer(chunk);
        }

        loadNext();
    });
}



export const fetchWithCSRF = async (url: string, options = {}) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    return fetch(url, {
        ...options,
        headers: {
            'X-CSRF-Token': csrfToken,
            // @ts-ignore
            ...options.headers,
        },
    });
};
